import { useEffect, useState } from 'react'
import { type CETAClassroomAndFocusStudents } from '../../../../../models/CETAClassroomAndFocusStudents'
import { type UpdateCetaClassroomAndFocusStudentsFormValues, type ClassroomFormValues } from './CetaClassroomsandFocusStudentsModal'
import { Accordion, AccordionSummary, AccordionDetails, Stack, Typography, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Checkbox, TextField, InputAdornment, Box } from '@mui/material'
import { toast } from 'react-toastify'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import { UpdateCetaClassroomAndFocusStudents } from '../../../../../services/AnnualPlanService'
import { Modal } from '../../../../customComponents/Modal'
import { ExpandMore } from '@mui/icons-material'
import { DatePickerWithLabel } from '../../../../customComponents/DatePickerWithLabel'

export interface CETAClassroomAndFocusStudentTrackingFormValues {
  classrooms: Array<ClassroomFormValues & { cetaCompleted: boolean }>
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents
  implementationPhase: boolean
  editClassroomNumber?: number
}
export const CETAClassroomAndFocusStudentTrackingModal = (props: Props): JSX.Element => {
  const isUpdate = props.cetaClassroomAndFocusStudents.isUpdate ?? false
  const [formValues, setFormValues] = useState<CETAClassroomAndFocusStudentTrackingFormValues>({
    classrooms: props.cetaClassroomAndFocusStudents.classrooms?.map(c => ({
      id: c.id,
      teacherName: c.teacherName,
      gradeLevels: c.gradeLevels,
      rcnContactName: c.rcnContactName,
      rcnContactEmail: c.rcnContactEmail,
      cetaCompletedInStart: c.cetaCompletedInStart,
      cetaCompletionDate: c.cetaKickoffCompletedDate != null ? new Date(c.cetaKickoffCompletedDate) : null,
      budgetSpent: c.budgetSpent,
      focusStudents: c.focusStudents.map(fs => ({
        id: fs.id,
        initials: fs.initials,
        eoyDataSubmitted: fs.eoyDataSubmitted,
        boyDataSubmitted: fs.boyDataSubmitted
      })),
      cetaCompleted: c.cetaCompletedInStart != null && c.cetaKickoffCompletedDate != null
    })) ?? []
  })
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(null)

  useEffect(() => {
    if (props.editClassroomNumber != null) {
      const classroomIndex = props.cetaClassroomAndFocusStudents.classrooms?.findIndex(c => c.id === props.editClassroomNumber) ?? -1
      setExpandedAccordion(classroomIndex)
    }
  }, [props.editClassroomNumber])

  const classroomFormValidator = (index: number): Record<keyof ClassroomFormValues, { required: boolean, isValid: boolean }> => {
    const classroom = formValues.classrooms[index]
    return {
      id: { required: false, isValid: true },
      teacherName: { required: true, isValid: classroom.teacherName.trim() !== '' },
      gradeLevels: { required: true, isValid: classroom.gradeLevels.trim() !== '' },
      rcnContactName: { required: true, isValid: classroom.rcnContactName.trim() !== '' },
      rcnContactEmail: { required: true, isValid: classroom.rcnContactEmail.trim() !== '' },
      focusStudents: { required: true, isValid: classroom.focusStudents.every(fs => fs.initials.trim() !== '') },
      // tracking fields are not required
      cetaCompletedInStart: { required: classroom.cetaCompleted, isValid: true },
      cetaCompletionDate: { required: classroom.cetaCompleted, isValid: classroom.cetaCompletionDate != null && new Date(classroom.cetaCompletionDate) instanceof Date && !isNaN(new Date(classroom.cetaCompletionDate).getTime()) },
      budgetSpent: { required: false, isValid: classroom.budgetSpent == null || classroom.budgetSpent === '' || isValidPositiveNumber(classroom.budgetSpent) }
    }
  }
  const hasInvalidClassroomFormValues = (classroomIndex: number): boolean => {
    return Object.values(classroomFormValidator(classroomIndex)).some(v => v.required && !v.isValid)
  }
  const hasInvalidFormValues = formValues.classrooms.length === 0 || formValues.classrooms.some((_, i) => hasInvalidClassroomFormValues(i))

  const handleClassroomFieldUpdate = (index: number, e: { target: { name: string, value: string | boolean | Date | null } }): void => {
    const { name, value } = e.target
    const updatedClassrooms = [...formValues.classrooms]

    if (name === 'cetaCompleted') {
      const isCompleted = value === 'true'
      updatedClassrooms[index] = {
        ...updatedClassrooms[index],
        cetaCompleted: isCompleted,
        cetaCompletedInStart: isCompleted ? updatedClassrooms[index].cetaCompletedInStart : null,
        cetaCompletionDate: isCompleted ? updatedClassrooms[index].cetaCompletionDate : null
      }
    }

    updatedClassrooms[index] = { ...updatedClassrooms[index], [name]: value }
    setFormValues({ ...formValues, classrooms: updatedClassrooms })
  }

  const handleFocusStudentFieldUpdate = (classroomIndex: number, fsIndex: number, e: { target: { name: string, checked: boolean } }): void => {
    const { name, checked } = e.target
    const updatedClassrooms = [...formValues.classrooms]
    const updatedFocusStudents = [...updatedClassrooms[classroomIndex].focusStudents]
    updatedFocusStudents[fsIndex] = { ...updatedFocusStudents[fsIndex], [name]: checked }
    updatedClassrooms[classroomIndex] = { ...updatedClassrooms[classroomIndex], focusStudents: updatedFocusStudents }
    setFormValues({ ...formValues, classrooms: updatedClassrooms })
  }

  const handleSubmit = async (): Promise<void> => {
    if (!isUpdate) {
      toast.error('This CETA Classrooms and Focus Students has no updates to track.')
      props.onClose()
      return
    }

    setSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const updatedClassroomsAndFocusStudents: UpdateCetaClassroomAndFocusStudentsFormValues = {
      id: props.cetaClassroomAndFocusStudents.id,
      priorityId: props.cetaClassroomAndFocusStudents.priorityId,
      organizationType: props.cetaClassroomAndFocusStudents.organizationType,
      organizationId: props.cetaClassroomAndFocusStudents.organization.id,
      buildingId: props.cetaClassroomAndFocusStudents.building.id,
      numberOfClassrooms: props.cetaClassroomAndFocusStudents.numberOfClassrooms,
      numberOfFocusStudents: props.cetaClassroomAndFocusStudents.numberOfFocusStudents,
      budgetRequest: props.cetaClassroomAndFocusStudents.budgetRequest,
      budgetRequestReason: props.cetaClassroomAndFocusStudents.budgetRequestReason,
      classrooms: formValues.classrooms.map(classroom => ({
        id: classroom.id,
        teacherName: classroom.teacherName,
        gradeLevels: classroom.gradeLevels,
        rcnContactName: classroom.rcnContactName,
        rcnContactEmail: classroom.rcnContactEmail,
        cetaCompletedInStart: classroom.cetaCompletedInStart,
        cetaKickoffCompletedDate: classroom.cetaCompleted ? classroom.cetaCompletionDate : null,
        budgetSpent: isValidPositiveNumber(classroom.budgetSpent) ? Number(classroom.budgetSpent) : null,
        focusStudents: classroom.focusStudents.map(fs => ({
          id: fs.id,
          initials: fs.initials,
          boyDataSubmitted: fs.boyDataSubmitted,
          eoyDataSubmitted: fs.eoyDataSubmitted
        }))
      })),
      isUpdate: true,
      isImplementationPhase: props.implementationPhase
    }

    const { success, errors } = await UpdateCetaClassroomAndFocusStudents(updatedClassroomsAndFocusStudents)

    if (!success) {
      errors?.forEach(e => toast.error(e))
      return
    }

    toast.success('CETA Classrooms and Focus Students Tracked')
    props.onSaved()
    props.onClose()
  }

  const renderTrackingFields = (): JSX.Element => {
    if (!isUpdate) {
      return <Typography variant='body1'>This CETA Classrooms and Focus Students has no updates to track.</Typography>
    }
    return <>
      {formValues.classrooms.map((classroom, classroomIndex) => {
        const isValid = !hasInvalidClassroomFormValues(classroomIndex)
        return (
          <Stack
            key={classroomIndex}
            gap='1rem'
            sx={{
              border: '1px solid var(--figma-tablegrey)',
              borderRadius: '12px'
            }}
          >
            <Accordion
              expanded={expandedAccordion === classroomIndex}
              onChange={(_, isExpanded) => { setExpandedAccordion(isExpanded ? classroomIndex : null) }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`classroomAccordionContent-${classroomIndex}`}
                id={`classroomAccordionHeader-${classroomIndex}`}
                data-testid={`classroomAccordionHeader-${classroomIndex}`}
              >
                <Typography
                  component="span"
                  variant='h5'
                  fontSize='18px'
                  color={theme => submitted && !isValid ? theme.palette.error.main : 'var(--black)'}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  Focus Classroom {classroomIndex + 1}
                  <Typography component="span" variant='body1'>{classroom.teacherName}</Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Typography variant='h4'>CETA</Typography>
                <FormControl>
                  <FormLabel id="cetaCompletedLabel">Was the CETA completed?</FormLabel>
                  <RadioGroup
                    aria-labelledby="cetaCompletedLabel"
                    name="cetaCompleted"
                    value={classroom.cetaCompleted}
                    onChange={(e) => { handleClassroomFieldUpdate(classroomIndex, { target: { name: 'cetaCompleted', value: e.target.value === 'true' } }) }}
                    data-testid={`cetaCompleted-${classroomIndex}`}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>

                {classroom.cetaCompleted && (
                  <>
                    <FormControl>
                      <FormLabel id="cetaCompletedPDSLabel">Was the CETA completed in the START Planning and Data System (PDS)?</FormLabel>
                      <RadioGroup
                        aria-labelledby="cetaCompletedPDSLabel"
                        name="cetaCompletedInStart"
                        value={classroom.cetaCompletedInStart}
                        onChange={(e) => { handleClassroomFieldUpdate(classroomIndex, { target: { name: 'cetaCompletedInStart', value: e.target.value === 'true' } }) }}
                        data-testid={`cetaCompletedInStart-${classroomIndex}`}
                      >
                        <FormControlLabel value={true} control={<Radio />} label="Yes" data-testid={`cetaCompletedInStart-yes-${classroomIndex}`} />
                        <FormControlLabel value={false} control={<Radio />} label="No" data-testid={`cetaCompletedInStart-no-${classroomIndex}`} />
                      </RadioGroup>
                    </FormControl>

                    <Box>
                      <Typography variant='body1'>When was the first CETA completed this school year?</Typography>
                      <DatePickerWithLabel
                        required
                        FormControlProps={{ variant: 'filled', sx: { marginTop: '.75em', width: '225px' } }}
                        name='cetaCompletionDate'
                        label='Session Date'
                        error={submitted && !classroomFormValidator(classroomIndex).cetaCompletionDate.isValid}
                        dataTestId={`cetaCompletionDate-${classroomIndex}`}
                        onChange={(e) => { handleClassroomFieldUpdate(classroomIndex, { target: { name: 'cetaCompletionDate', value: e } }) }}
                        value={classroom.cetaCompletionDate ?? null}
                        data-testid={`cetaCompletionDate-${classroomIndex}`}
                      />
                    </Box>
                  </>
                )}

                <Typography variant='h4'>Focus Student(s)</Typography>
                <Typography variant='body1'>Please verify that the corresponding data has been submitted for each student listed.</Typography>
                {classroom.focusStudents.map((fs, fsIndex) => (
                  <Stack
                    key={fsIndex}
                    gap='0.5rem'
                    direction='row'
                    alignItems='center'
                    data-testid={`focusStudent-${classroomIndex}`}
                  >
                    <Typography variant='body1' mr='1rem'>{fs.initials}</Typography>

                    <FormControlLabel
                      name='boyDataSubmitted'
                      control={<Checkbox />}
                      checked={fs.boyDataSubmitted ?? false}
                      onChange={(e) => { handleFocusStudentFieldUpdate(classroomIndex, fsIndex, { target: { name: 'boyDataSubmitted', checked: (e.target as HTMLInputElement).checked } }) }}
                      label="BOY"
                      data-testid={`boy-${classroomIndex}`}
                    />

                    <FormControlLabel
                      name='eoyDataSubmitted'
                      control={<Checkbox />}
                      checked={fs.eoyDataSubmitted ?? false}
                      onChange={(e) => { handleFocusStudentFieldUpdate(classroomIndex, fsIndex, { target: { name: 'eoyDataSubmitted', checked: (e.target as HTMLInputElement).checked } }) }}
                      label="EOY"
                      data-testid={`eoy-${classroomIndex}`}
                    />
                  </Stack>
                ))}

                <Typography variant='h5'>Budget</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Typography variant='body1'>
                    How much funding was actually used?
                    <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Optional</Typography>
                  </Typography>
                  <TextField
                    id='budgetSpent'
                    sx={{ mt: '.75em', width: '250px' }}
                    variant='filled'
                    onChange={(e) => { handleClassroomFieldUpdate(classroomIndex, e) }}
                    label={'Enter Amount'}
                    data-testid={`budgetSpent-${classroomIndex}`}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    name='budgetSpent'
                    required={classroomFormValidator(classroomIndex).budgetSpent.required}
                    error={submitted && !classroomFormValidator(classroomIndex).budgetSpent.isValid}
                    value={classroom.budgetSpent ?? ''}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )
      })}
    </>
  }

  return (
    <Modal
      title='Tracking CETA Classrooms & Focus Students'
      open={props.open}
      data-testid='cetaClassroomsAndFocusStudentsTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>
        {renderTrackingFields()}
      </Stack>}
    />
  )
}
